import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({
    title: "Are you sure?",
    description: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    destructive: false,
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({
      title: options.title || "Are you sure?",
      description: options.description || "",
      confirmText: options.confirmText || "Confirm",
      cancelText: options.cancelText || "Cancel",
      destructive: options.destructive ?? false,
    });
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleAnswer = (val) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(val);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => { if (!v) handleAnswer(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-dialog-title">{opts.title}</AlertDialogTitle>
            {opts.description ? (
              <AlertDialogDescription data-testid="confirm-dialog-description">
                {opts.description}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-dialog-cancel"
              onClick={() => handleAnswer(false)}
            >
              {opts.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-confirm"
              onClick={() => handleAnswer(true)}
              className={
                opts.destructive
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : ""
              }
            >
              {opts.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback (should not happen if provider is mounted) — resolves true to keep flows working
    return async () => true;
  }
  return ctx;
};
